import { isNil } from 'lodash-es';
import type React from 'react';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { IoIosPerson } from 'react-icons/io';
import { lazyReactNaiveRetry } from '../../../constants/Lazy';
import useAvailableBusinessesModal from '../../../hooks/useAvailableBusinessesModal';
import type { PartnerDetailsDescriptor } from '../../../redux/ducks/SearchDucks.types';
import { getEnvironmentVariable } from '../../../utils/EnvironmentHelper/functions';
import LazyBackground from '../../layout/LazyBackground/LazyBackground';
import type { MarkerProps } from '../../layout/Map';
import Spinner from '../../layout/Spinner/Spinner';

const CustomMap = lazyReactNaiveRetry(() => import('../../layout/Map'));

interface PartnerSeoHeroSectionProps {
  partnerProfile: PartnerDetailsDescriptor;
  selectedBusiness: string | undefined;
}

const PartnerSeoHeroSection: React.FC<PartnerSeoHeroSectionProps> = ({
  partnerProfile,
  selectedBusiness,
}) => {
  const availableBusinessesModal = useAvailableBusinessesModal();
  const [onClient, setOnClient] = useState<boolean>(false);

  useEffect(() => {
    setOnClient(true);

    return () => {
      setOnClient(false);
    };
  }, []);

  const map = useMemo(() => {
    if (onClient) {
      const markers: MarkerProps[] = [];
      if (partnerProfile) {
        for (let index = 0; index < partnerProfile.businesses.length; index++) {
          const business = partnerProfile.businesses[index];
          const businessMarker: MarkerProps = {
            id: business.id,
            active:
              !isNil(selectedBusiness) && business.id === selectedBusiness,
            lat: business.location.latitude,
            lng: business.location.longitude,
          };

          markers.push(businessMarker);
        }
      }

      const targetBusiness = partnerProfile.businesses.filter(
        (b) => b.active && b.id === selectedBusiness,
      );

      let mapCenter: number[] = [0, 0];
      if (targetBusiness.length === 0) {
        mapCenter = [0, 0];
        const activeBusinesses = partnerProfile.businesses.filter(
          (b) => b.active,
        );
        if (activeBusinesses.length > 0) {
          let latSum = 0;
          let lngSum = 0;
          for (const activeBusiness of activeBusinesses) {
            latSum += activeBusiness.location.latitude;
            lngSum += activeBusiness.location.longitude;
          }

          mapCenter = [
            latSum / activeBusinesses.length,
            lngSum / activeBusinesses.length,
          ];
        }
      } else {
        mapCenter = [
          targetBusiness[0].location.latitude,
          targetBusiness[0].location.longitude,
        ];
      }

      return (
        <Suspense fallback={<div className="hidden">&nbsp;</div>}>
          <CustomMap
            id="partner-0-profile-map"
            center={mapCenter}
            markers={markers}
            zoom={16}
            styled="rounded-b-2xl sm:rounded-r-2xl"
            scrollWheelZoom
            onMarkerClick={availableBusinessesModal.onOpen}
          />
        </Suspense>
      );
    }

    return null;
  }, [selectedBusiness, partnerProfile, availableBusinessesModal, onClient]);

  return (
    <div className="relative grid aspect-square size-full h-full grid-cols-1 gap-0 rounded-2xl sm:max-h-[400px] sm:grid-cols-2 lg:max-h-[440px]">
      <span className="absolute left-0 top-0 z-0 flex size-full flex-col rounded-2xl border-0 sm:flex-row">
        <span className="flex size-full h-1/2 flex-col items-center justify-center sm:h-full sm:w-1/2">
          {partnerProfile?.media?.profile_picture ? (
            <Spinner color="fill-sky-600" />
          ) : (
            <span className="flex size-full flex-col items-center justify-center rounded-t-2xl border-0 bg-slate-100 text-white sm:rounded-l-2xl">
              <IoIosPerson size={120} />
            </span>
          )}
        </span>
        <span className="flex size-full h-1/2 flex-col items-center justify-center sm:h-full sm:w-1/2">
          <Spinner color="fill-sky-600" />
        </span>
      </span>
      <div className="z-10 h-full items-start justify-start sm:mr-1">
        {partnerProfile?.media?.profile_picture && (
          <LazyBackground
            className="size-full rounded-t-2xl border-0 bg-cover bg-center sm:rounded-l-2xl"
            src={`//${getEnvironmentVariable('REACT_APP_CDN_API')}${
              partnerProfile?.media?.profile_picture
            }`}
          />
        )}
      </div>
      <span className="z-10 h-full items-end justify-start pt-2 sm:ml-1 sm:pt-0">
        {onClient && <div className="size-full">{map}</div>}
      </span>
    </div>
  );
};

export default PartnerSeoHeroSection;
